import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";

// Phase 4q portal-ids-polish — inline form to add an authorized
// individual (someone other than the customer who may operate
// equipment on their behalf). The controller sanitises `relationship`
// server-side, so the customer-facing select only exposes the four
// non-self enum values from AuthorizedIndividual::RELATIONSHIPS;
// `self` is created by the CustomerProfile callback at signup and is
// immutable from the portal.
//
// On success the parent's `["portal", "ids"]` query is invalidated so
// the new individual appears in the list immediately.

// AuthorizedIndividual::RELATIONSHIPS — matches
// app/models/authorized_individual.rb. Picking a value outside this
// set would silently get coerced to `employee` by the controller's
// `sanitise_relationship`, so the dropdown labels must match the
// model's enum exactly.
const RELATIONSHIP_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "employee",      label: "Employee" },
  { value: "family",        label: "Family" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "other",         label: "Other" }
];

interface Props {
  onClose: () => void;
}

export function AuthorizedIndividualForm({ onClose }: Props): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("employee");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const csrf =
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
      const res = await fetch("/portal/account/authorized_individuals.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          Accept: "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          name,
          relationship,
          phone_e164: phone || null,
          email: email || null
        })
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? "Could not add this person.");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal", "ids"] });
      onClose();
    }
  });

  return (
    <form
      data-testid="add-individual-form"
      className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <p className="text-sm font-semibold text-brand-black">Add an authorized individual</p>

      <label className="block text-xs font-medium text-zinc-700">
        Name
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="ai-name"
          className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy"
          placeholder="Jane Smith"
          disabled={mutation.isPending}
        />
      </label>

      <label className="block text-xs font-medium text-zinc-700">
        Relationship
        <select
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy"
          disabled={mutation.isPending}
        >
          {RELATIONSHIP_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-medium text-zinc-700">
          Phone <span className="font-normal text-zinc-500">(optional)</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="ai-phone"
            className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy"
            placeholder="+1 555 123 4567"
            disabled={mutation.isPending}
          />
        </label>

        <label className="block text-xs font-medium text-zinc-700">
          Email <span className="font-normal text-zinc-500">(optional)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="ai-email"
            className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy"
            placeholder="jane@example.com"
            disabled={mutation.isPending}
          />
        </label>
      </div>

      {mutation.isError ? (
        <div
          role="alert"
          className="rounded-md border border-brand-red/40 bg-brand-red/5 px-3 py-2 text-sm text-brand-red"
        >
          {(mutation.error as Error).message}
        </div>
      ) : null}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          data-testid="ai-save"
          disabled={mutation.isPending || !name.trim()}
        >
          {mutation.isPending ? "Adding…" : "Add individual"}
        </Button>
      </div>
    </form>
  );
}
